import { baseService } from "../base";

// Types for storage calculation
export interface StorageSnapshot {
  date: string;
  cubicFootage: number;
  inventoryValue: number;
  itemCount: number;
}

export interface StorageCalculationResult {
  averageCubicFootage: number;
  storageRate: number;
  includeInsurance: boolean;
  insuranceRate: number | null;
  totalInventoryValue: number;
  snapshots: StorageSnapshot[];
}

export interface ProcessMonthlyStorageResponse {
  successful: number;
  failed: number;
  details: {
    clientId: string;
    clientName: string;
    success: boolean;
    message?: string;
  }[];
}

export interface ClientStorageSettings {
  id: string;
  clientId: string;
  storageRate: number;
  includeInsurance: boolean;
  insuranceRate?: number;
  isActive: boolean;
}

export interface CreateStorageCalculationRequest {
  clientId: string;
  year: number;
  month: number;
}

export interface ProcessStorageInvoicesRequest {
  year: number;
  month: number;
  clientId?: string;
}

export interface ScheduledJob {
  name: string;
  schedule: string;
  description: string;
  lastRun: string | null;
  nextRun: string;
  status: 'active' | 'inactive' | 'error';
}

export interface JobsStatusResponse {
  jobs: ScheduledJob[];
}

export interface JobTriggerResponse {
  success: boolean;
  message: string;
}

// Calculate monthly storage for a specific client
export const calculateMonthlyStorage = async (
  clientId: string,
  year: number,
  month: number
): Promise<StorageCalculationResult> => {
  const { data } = await baseService.post('/api/storage/calculations', {
    clientId,
    year,
    month
  });
  
  return data;
};

// Get client storage settings
export const getClientStorageSettings = async (clientId: string): Promise<ClientStorageSettings> => {
  const { data } = await baseService.get(`/api/storage/clients/${clientId}/settings`);
  return data;
};

// Update client storage settings
export const updateClientStorageSettings = async (
  clientId: string,
  settings: Partial<ClientStorageSettings>
): Promise<ClientStorageSettings> => {
  const { data } = await baseService.put(`/api/storage/clients/${clientId}/settings`, settings);
  return data;
};

// Get storage calculations with filters
export const getStorageCalculations = async (
  clientId?: string,
  year?: number,
  month?: number
): Promise<StorageCalculationResult[]> => {
  const { data } = await baseService.get('/api/storage/calculations', {
    params: { clientId, year, month }
  });
  return data;
};

// Process monthly storage invoices for all clients
export const processMonthlyStorageInvoices = async (
  request?: ProcessStorageInvoicesRequest
): Promise<{ processed: number; errors: string[] }> => {
  const { data } = await baseService.post('/api/storage/invoices/process', request || {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1
  });
  return data;
};

// Create daily inventory snapshot
export const createDailySnapshot = async (
  clientId: string,
  date?: string
): Promise<any> => {
  const { data } = await baseService.post(`/api/storage/snapshots/${clientId}`, {
    date
  });
  return data;
};

// ========================= JOB MANAGEMENT APIS =========================

// Get scheduled jobs status
export const getJobsStatus = async (): Promise<JobsStatusResponse> => {
  const { data } = await baseService.get('/api/storage/jobs/status');
  return data;
};

// Trigger daily snapshots job manually
export const triggerDailySnapshotsJob = async (): Promise<JobTriggerResponse> => {
  const { data } = await baseService.post('/api/storage/jobs/snapshots/trigger');
  return data;
};

// Trigger monthly invoices job manually
export const triggerMonthlyInvoicesJob = async (): Promise<JobTriggerResponse> => {
  const { data } = await baseService.post('/api/storage/jobs/invoices/trigger');
  return data;
};

// Trigger invoice sync job manually
export const triggerInvoiceSyncJob = async (): Promise<JobTriggerResponse> => {
  const { data } = await baseService.post('/api/storage/jobs/sync/trigger');
  return data;
};

// Trigger storage calculations job manually
export const triggerStorageCalculationsJob = async (): Promise<JobTriggerResponse> => {
  const { data } = await baseService.post('/api/storage/jobs/calculations/trigger');
  return data;
};

// Calculate storage for client and month
export const calculateStorageForMonth = async (request: {
  clientId: string;
  year: number;
  month: number;
}): Promise<StorageCalculationResult> => {
  const { data } = await baseService.post('/api/storage/calculations', request);
  return data;
};

// ========================= STORAGE INVOICES =========================

export enum StorageInvoiceStatus {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PAID = 'PAID',
  CANCELLED = 'CANCELLED',
}

export interface StorageInvoiceLineItem {
  locationId?: string;
  locationName: string;
  description: string;
  actualCubicFt?: number;
  minimumCubicFt?: number;
  billableCubicFt?: number;
  ratePerCubicFt?: number;
  amount: number;
  type: 'storage' | 'insurance' | 'other';
}

export interface StorageInvoice {
  id: string;
  invoiceNumber: string;
  clientId: string;
  client?: {
    id: string;
    companyName: string;
    firstName?: string;
    lastName?: string;
  };
  billingPeriodMonth: number;
  billingPeriodYear: number;
  invoiceDate: string;
  dueDate?: string;
  status: StorageInvoiceStatus;
  lineItems: StorageInvoiceLineItem[];
  storageTotal: number;
  insuranceTotal: number;
  taxRate?: number;
  taxAmount: number;
  totalAmount: number;
  notes?: string;
  paidDate?: string;
  paidAmount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStorageInvoiceRequest {
  clientId: string;
  billingPeriodMonth: number;
  billingPeriodYear: number;
  invoiceDate: string;
  dueDate?: string;
  lineItems: StorageInvoiceLineItem[];
  storageTotal: number;
  insuranceTotal?: number;
  taxRate?: number;
  taxAmount?: number;
  totalAmount: number;
  notes?: string;
}

export interface GetStorageInvoicesParams {
  clientId?: string;
  year?: number;
  month?: number;
  status?: StorageInvoiceStatus;
  page?: number;
  limit?: number;
}

export interface GetStorageInvoicesResponse {
  invoices: StorageInvoice[];
  total: number;
  page: number;
  limit: number;
}

// Create a new storage invoice
export const createStorageInvoice = async (
  request: CreateStorageInvoiceRequest
): Promise<StorageInvoice> => {
  const { data } = await baseService.post('/api/storage/storage-invoices', request);
  return data;
};

// Get storage invoices with filters
export const getStorageInvoices = async (
  params: GetStorageInvoicesParams
): Promise<GetStorageInvoicesResponse> => {
  const { data } = await baseService.get('/api/storage/storage-invoices', { params });
  return data;
};

// Get single storage invoice by ID
export const getStorageInvoiceById = async (id: string): Promise<StorageInvoice> => {
  const { data } = await baseService.get(`/api/storage/storage-invoices/${id}`);
  return data;
};

// Update storage invoice
export const updateStorageInvoice = async (
  id: string,
  updates: Partial<CreateStorageInvoiceRequest> & { status?: StorageInvoiceStatus }
): Promise<StorageInvoice> => {
  const { data } = await baseService.put(`/api/storage/storage-invoices/${id}`, updates);
  return data;
};

// Delete storage invoice
export const deleteStorageInvoice = async (id: string): Promise<void> => {
  await baseService.delete(`/api/storage/storage-invoices/${id}`);
};

// Mark invoice as sent
export const markStorageInvoiceAsSent = async (id: string): Promise<StorageInvoice> => {
  const { data } = await baseService.post(`/api/storage/storage-invoices/${id}/mark-sent`);
  return data;
};

// Mark invoice as paid
export const markStorageInvoiceAsPaid = async (
  id: string,
  paidAmount?: number,
  paidDate?: string
): Promise<StorageInvoice> => {
  const { data } = await baseService.post(`/api/storage/storage-invoices/${id}/mark-paid`, {
    paidAmount,
    paidDate,
  });
  return data;
};
